import React, { useState, useEffect } from 'react';
import {
  LegacyAutocomplete,
  LegacyAutocompleteInput,
  LegacyAutocompleteOptions,
  LegacyAutocompleteSelectedOptions,
} from '@flixbus/honeycomb-react';

import { useVoucherFormContext } from '../../Context';
import { LineBlacklistRule, LineWhitelistRule } from '../../../../types/Rules';
import { getLines, getLineById } from '../../../../apis';
import { Line } from '../../../../types/Line';

interface AutocompleteLine {
  title: string;
  subtitle?: string;
}

const convertLinesToAutoCompleteLines = (lines: Line[]): AutocompleteLine[] => {
  return lines.map(line => ({
    title: `${line.code} - ${line.title}`,
    subtitle: line.lineId,
  }));
};

type LinesAutocompleteProps = {
  label: string;
  ruleType: 'line_whitelist' | 'line_blacklist';
};

const LinesAutocomplete = ({ label, ruleType }: LinesAutocompleteProps) => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
  } = useVoucherFormContext();

  const [data, setData] = useState<AutocompleteLine[]>([]);
  const [selectedLines, setSelectedLines] = useState<AutocompleteLine[]>([]);
  const [highlight, setHighlight] = useState('');

  // Load existing lines when editing a voucher
  useEffect(() => {
    const lineIdsState =
      (
        rules.filter(r => r.type === ruleType)[0] as
          | LineWhitelistRule
          | LineBlacklistRule
          | undefined
      )?.lines || [];

    if (lineIdsState.length > 0) {
      Promise.all(lineIdsState.map(lineId => getLineById(lineId)))
        .then(lines => {
          setSelectedLines(convertLinesToAutoCompleteLines(lines));
        })
        .catch(() => {
          setSelectedLines([]);
        });
    } else {
      setSelectedLines([]);
    }
  }, [rules, ruleType]);

  useEffect(() => {
    if (highlight) {
      getLines(highlight).then(lines => {
        setData(convertLinesToAutoCompleteLines(lines));
      });
    } else {
      setData([]);
    }
  }, [highlight]);

  const handleValueChange = (lines: AutocompleteLine[]) => {
    const lineIds = lines.map(line => line.subtitle).filter((id): id is string => !!id);
    handleRuleChange(
      {
        type: ruleType,
        lines: lineIds,
      },
      lineIds.length === 0,
    );
    setSelectedLines(lines);
  };

  const handleItemSelect = (selectedLine: { title: string; subtitle?: string }) => {
    const lineWithId = data.find(d => d.title === selectedLine.title);
    if (lineWithId && !selectedLines.some(line => line.subtitle === lineWithId.subtitle)) {
      handleValueChange([...selectedLines, lineWithId]);
      setHighlight('');
      setData([]);
    }
  };

  const handleItemDelete = (selectedLine: { title: string; subtitle?: string }) => {
    handleValueChange(selectedLines.filter(line => line.title !== selectedLine.title));
  };

  const handleReset = () => {
    handleValueChange([]);
  };

  return (
    <LegacyAutocomplete
      options={data}
      optionsSelected={selectedLines.map(line => ({ title: line.title }))}
      onDebounce={(e: React.ChangeEvent<HTMLInputElement>) => setHighlight(e.target.value)}
      onDelete={handleItemDelete}
      onReset={handleReset}
      onSelect={handleItemSelect}
    >
      <LegacyAutocompleteInput id='autocomplete-lines' label={label} type='search' />
      <LegacyAutocompleteOptions label='Lines' highlightQuery={highlight} />
      <LegacyAutocompleteSelectedOptions
        deleteAllItemsLabel='Clear selection'
        deleteItemLabel='Remove item from selection'
      />
    </LegacyAutocomplete>
  );
};

export default LinesAutocomplete;
